import { FC, PropsWithChildren, useCallback } from "react";

import { OrganizationRoutes } from "@simplyk/common";
import { useRouter } from "next/router";

import { useCurrentUserContext } from "../../../common/contexts/CurrentUserContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useRedirectToStripeButton } from "../../features/StripeOnboarding/useRedirectToStripeButton";

import { DashboardButton, DashboardButtonProps } from "@/dashboard-design-system/DashboardButton";
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";

export interface ConnectStripeButtonProps extends DashboardButtonProps {
  isCustomElement?: boolean;
  disabled?: boolean;
}

export const ConnectStripeButton: FC<PropsWithChildren<ConnectStripeButtonProps>> = ({
  children,
  disabled = false,
  isCustomElement = false,
  ...rest
}) => {
  const router = useRouter();
  const { organizationHasFinishZeffyOnboarding } = useCurrentUserContext();

  const { getRedirectionLink, isLoading, setIsLoading } = useRedirectToStripeButton();

  const handleRedirection = useCallback(async () => {
    if (disabled || isLoading) {
      return;
    }
    const redirect = await getRedirectionLink();
    if (redirect) {
      await router.push(redirect);
      setIsLoading(false);
    }
  }, [disabled, getRedirectionLink, isLoading, router, setIsLoading]);

  if (organizationHasFinishZeffyOnboarding) {
    return isCustomElement ? (
      <>
        {!isLoading && <span onClick={handleRedirection}>{children}</span>}
        {isLoading && <DashboardZeffyLoader size="small" />}
      </>
    ) : (
      <DashboardButton
        {...rest}
        endIcon={rest.endIcon}
        disabled={disabled}
        onClick={handleRedirection}
        isLoading={isLoading}
      >
        {children}
      </DashboardButton>
    );
  }

  return (
    <>
      {isCustomElement && <a href={OrganizationRoutes.StripeOnboarding}>{children}</a>}
      {!isCustomElement && (
        <DashboardButton
          {...rest}
          endIcon={rest.endIcon}
          disabled={disabled}
          href={OrganizationRoutes.StripeOnboarding}
        >
          {children}
        </DashboardButton>
      )}
    </>
  );
};
